import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { User, Lock, ShieldCheck, AlertCircle, CheckCircle2, Smartphone, QrCode } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Modal from "@/components/Modal";

export default function Settings() {
  const { user, refreshMe } = useAuth();
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // 2FA states
  const [show2faSetup, setShow2faSetup] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [twoFaMsg, setTwoFaMsg] = useState("");

  async function changePw(e) {
    e.preventDefault();
    setMsg(""); setErr("");
    if (pw !== pw2) return setErr("Les nouveaux mots de passe ne correspondent pas");
    if (pw.length < 8) return setErr("Mot de passe trop court (min 8)");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: pw });
      setMsg("Mot de passe modifié avec succès");
      setCurrent(""); setPw(""); setPw2("");
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  }

  async function start2fa() {
    setTwoFaMsg("");
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
      setShow2faSetup(true);
    } catch (e) { setTwoFaMsg(e?.response?.data?.detail || "Erreur"); }
  }

  async function enable2fa() {
    try {
      await api.post("/auth/2fa/enable", { code });
      setShow2faSetup(false);
      setCode("");
      setTwoFaMsg("✅ 2FA activé avec succès");
      refreshMe();
    } catch (e) { alert(e?.response?.data?.detail || "Code invalide"); }
  }

  async function disable2fa() {
    const c = prompt("Entrez votre code 2FA actuel pour désactiver :");
    if (!c) return;
    try {
      await api.post("/auth/2fa/disable", { code: c });
      setTwoFaMsg("2FA désactivé");
      refreshMe();
    } catch (e) { alert(e?.response?.data?.detail || "Erreur"); }
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-8 max-w-3xl" data-testid="settings-page">
      <section className="card p-6">
        <div className="flex items-center gap-2 mb-5">
          <User size={16} className="text-citron"/>
          <h2 className="font-heading text-lg font-bold">Profil</h2>
        </div>
        <dl className="grid grid-cols-2 gap-4 text-sm">
          <div><dt className="overline mb-1">Nom</dt><dd>{user?.full_name}</dd></div>
          <div><dt className="overline mb-1">Email</dt><dd className="font-mono text-xs">{user?.email}</dd></div>
          <div><dt className="overline mb-1">Rôle</dt><dd><span className="pill bg-citron text-obsidian-950">{user?.role}</span></dd></div>
          <div><dt className="overline mb-1">Entités</dt><dd className="text-xs">{user?.entity_ids?.length === 7 ? "Toutes" : (user?.entity_ids?.join(", ") || "—")}</dd></div>
        </dl>
      </section>

      <section className="card p-6">
        <div className="flex items-center gap-2 mb-5">
          <Lock size={16} className="text-citron"/>
          <h2 className="font-heading text-lg font-bold">Changer le mot de passe</h2>
        </div>
        <form onSubmit={changePw} className="space-y-4">
          <div><label className="label block">Mot de passe actuel</label><input type="password" required className="input" value={current} onChange={(e) => setCurrent(e.target.value)}/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label block">Nouveau</label><input type="password" required minLength={8} className="input" value={pw} onChange={(e) => setPw(e.target.value)}/></div>
            <div><label className="label block">Confirmer</label><input type="password" required minLength={8} className="input" value={pw2} onChange={(e) => setPw2(e.target.value)}/></div>
          </div>
          {err && <div className="flex items-start gap-2 p-3 bg-bad/10 border border-bad/30 rounded-sm text-bad text-xs"><AlertCircle size={14}/>{err}</div>}
          {msg && <div className="flex items-start gap-2 p-3 bg-ok/10 border border-ok/30 rounded-sm text-ok text-xs"><CheckCircle2 size={14}/>{msg}</div>}
          <button type="submit" disabled={loading} className="btn-primary">{loading ? "…" : "Modifier"}</button>
        </form>
      </section>

      <section className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Smartphone size={16} className="text-citron"/>
            <h2 className="font-heading text-lg font-bold">Authentification à 2 facteurs (2FA)</h2>
          </div>
          {user?.totp_enabled
            ? <span className="pill bg-ok/15 text-ok"><ShieldCheck size={10}/> Activé</span>
            : <span className="pill bg-obsidian-800 text-obsidian-400">Désactivé</span>}
        </div>
        <p className="text-sm text-obsidian-300 mb-5 leading-relaxed">
          Ajoute une couche de sécurité supplémentaire à votre compte. À chaque connexion, un code à 6 chiffres généré par votre application d'authentification (Google Authenticator, Authy, 1Password…) sera requis.
        </p>
        {twoFaMsg && <div className="p-3 bg-ok/10 border border-ok/30 rounded-sm text-ok text-xs mb-4">{twoFaMsg}</div>}
        {user?.totp_enabled
          ? <button onClick={disable2fa} className="btn-danger">Désactiver 2FA</button>
          : <button onClick={start2fa} className="btn-primary" data-testid="enable-2fa-button"><QrCode size={14}/> Activer 2FA</button>}
      </section>

      <section className="card p-6 border-citron/20">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={16} className="text-citron"/>
          <h2 className="font-heading text-lg font-bold">Sécurité de la plateforme</h2>
        </div>
        <ul className="text-sm text-obsidian-300 space-y-2">
          <li className="flex items-center gap-2"><CheckCircle2 size={12} className="text-ok"/> JWT 12h + bcrypt</li>
          <li className="flex items-center gap-2"><CheckCircle2 size={12} className="text-ok"/> RBAC granulaire par rôle × entité</li>
          <li className="flex items-center gap-2"><CheckCircle2 size={12} className="text-ok"/> Audit log immuable</li>
          <li className="flex items-center gap-2"><CheckCircle2 size={12} className="text-ok"/> Sauvegardes automatiques toutes les 5 min + complètes 2x/jour</li>
          <li className="flex items-center gap-2"><CheckCircle2 size={12} className="text-ok"/> Temps réel (WebSocket) pour collaboration live</li>
          <li className="flex items-center gap-2"><CheckCircle2 size={12} className="text-ok"/> 2FA TOTP disponible</li>
        </ul>
      </section>

      <Modal open={show2faSetup} onClose={() => { setShow2faSetup(false); setCode(""); }} title="Configurer l'authentification à 2 facteurs" size="md">
        {setupData && (
          <div className="space-y-5">
            <ol className="text-sm text-obsidian-300 space-y-3 list-decimal list-inside">
              <li>Installez <strong className="text-citron">Google Authenticator</strong>, Authy ou 1Password sur votre téléphone.</li>
              <li>Scannez ce QR code <em>(ou entrez la clé manuellement)</em> :</li>
            </ol>

            <div className="bg-white p-4 grid place-items-center rounded-sm">
              <img alt="QR" src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(setupData.otpauth_url)}`} />
            </div>

            <div>
              <label className="label block">Clé secrète (saisie manuelle)</label>
              <div className="font-mono text-sm bg-obsidian-900 p-3 rounded-sm break-all select-all">{setupData.secret}</div>
            </div>

            <div>
              <label className="label block">Entrez le code à 6 chiffres affiché par votre application</label>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="input font-mono text-2xl tracking-[0.5em] text-center"
                placeholder="000000"
                maxLength={6}
                data-testid="2fa-code-input"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => { setShow2faSetup(false); setCode(""); }} className="btn-secondary">Annuler</button>
              <button onClick={enable2fa} disabled={code.length !== 6} className="btn-primary" data-testid="2fa-enable-button">Activer 2FA</button>
            </div>
          </div>
        )}
      </Modal>
    </motion.div>
  );
}
